// src/pages/financials/BalanceSheetTab.tsx
//
// Balance Sheet — simple statement from the qbd-balance-sheet recipe.
// Assets / Liabilities / Equity sections with account rows and section
// totals, an "as of" date line, a balance check line (Assets = Liabilities
// + Equity, green when they agree within $1), and CSV export.

import { useQuery } from '@tanstack/react-query';
import { Scale, RefreshCw, Download, AlertTriangle, CheckCircle2, XCircle, FileSpreadsheet } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type { QbdBalanceSheetResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmt0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Whole-dollar currency; negatives red in accounting parentheses. */
function Amt({ v, bold = false }: { v: number; bold?: boolean }) {
  if (v < 0) {
    return <span className={`text-red-400 ${bold ? 'font-semibold' : ''}`}>({fmt0.format(Math.abs(v))})</span>;
  }
  return <span className={bold ? 'font-semibold' : ''}>{fmt0.format(v)}</span>;
}

interface FlatRow { label: string; balance: number | null }

function exportBalanceSheet(data: QbdBalanceSheetResponse, exporter: typeof exportCsv = exportCsv) {
  const rows: FlatRow[] = [];
  for (const s of data.sections) {
    rows.push({ label: s.label, balance: null });
    for (const a of s.accounts) rows.push({ label: `  ${a.account}`, balance: a.balance });
    rows.push({ label: `Total ${s.label}`, balance: s.total });
  }
  rows.push({ label: 'Total Liabilities + Equity', balance: data.totalLiabilities + data.totalEquity });
  const cols: CsvColumns<FlatRow> = [
    ['Account', (r) => r.label],
    [`Balance (as of ${data.asOf})`, (r) => r.balance ?? ''],
  ];
  exporter('balance-sheet', cols, rows);
}

export function BalanceSheetTab() {
  const q = useQuery({
    queryKey: ['qbd', 'balance-sheet'],
    queryFn: () => api.getQbdBalanceSheet(),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const balances = data ? Math.abs(data.totalAssets - (data.totalLiabilities + data.totalEquity)) < 1 : false;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Scale size={20} /> Balance Sheet
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Assets, liabilities, and equity from QuickBooks Desktop
          {data && <span className="text-slate-500"> · as of {data.asOf}</span>}
        </p>
      </div>

      {/* Control bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        {data && (
          <div className={`flex items-center gap-1.5 text-xs ${balances ? 'text-emerald-400' : 'text-red-400'}`}>
            {balances ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            <span>
              Assets {fmt0.format(data.totalAssets)} {balances ? '=' : '≠'} Liabilities + Equity {fmt0.format(data.totalLiabilities + data.totalEquity)}
            </span>
          </div>
        )}
        <div className="flex-1" />
        <button
          onClick={() => data && exportBalanceSheet(data)}
          disabled={!data}
          title="Export the balance sheet to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> Export
        </button>
        <button
          onClick={() => data && exportBalanceSheet(data, exportXlsx)}
          disabled={!data}
          title="Export to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the balance sheet</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium min-w-[220px]">Account</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.sections.map((s) => (
                  <SectionRows key={s.key} label={s.label} accounts={s.accounts} total={s.total} />
                ))}
                {/* Bottom line: liabilities + equity vs assets */}
                <tr className="border-t-2 border-slate-500/60 bg-slate-800/60">
                  <td className="px-3 py-2.5 font-bold text-white whitespace-nowrap">Total Liabilities + Equity</td>
                  <td className="px-3 py-2.5 text-right text-white tabular-nums whitespace-nowrap">
                    <Amt v={data.totalLiabilities + data.totalEquity} bold />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {data.sections.every((s) => s.accounts.length === 0) && (
            <div className="text-center py-8 text-slate-500 text-sm">No balance sheet accounts</div>
          )}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
            As of {data.asOf}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionRows({ label, accounts, total }: { label: string; accounts: { account: string; balance: number }[]; total: number }) {
  return (
    <>
      <tr className="border-b border-slate-800/50 bg-slate-800/60">
        <td className="px-3 py-2 font-semibold text-white" colSpan={2}>{label}</td>
      </tr>
      {accounts.map((a) => (
        <tr key={a.account} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
          <td className="px-3 py-2 pl-8 text-slate-300 whitespace-nowrap">{a.account}</td>
          <td className="px-3 py-2 text-right text-slate-300 tabular-nums whitespace-nowrap"><Amt v={a.balance} /></td>
        </tr>
      ))}
      <tr className="border-b border-slate-700/50 bg-slate-800/40">
        <td className="px-3 py-2 pl-8 font-medium text-slate-200 whitespace-nowrap">Total {label}</td>
        <td className="px-3 py-2 text-right text-white tabular-nums whitespace-nowrap"><Amt v={total} bold /></td>
      </tr>
    </>
  );
}
